import React from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import parse from 'html-react-parser';

export default function JDDetailsModal({ open, onClose, jd }) {
    if (!jd) return null;

    const InfoRow = ({ label, value }) => {
        if (!value) return null;
        return (
            <div className="flex gap-2 text-gray-700">
                <div className="min-w-[140px] font-medium text-gray-900">{label}</div>
                <div className="flex-1">{value}</div>
            </div>
        );
    };

    const Section = ({ title, children }) => (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div
                className="px-4 py-2 text-white font-semibold"
                style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
            >
                {title}
            </div>
            <div className="bg-white p-4">
                {children}
            </div>
        </div>
    );

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle 
                sx={{ 
                    m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
                    color: '#fff'
                }}
            >
                <div className="flex items-center gap-2">
                    <span 
                        className="px-2 py-0.5 rounded-md text-xs font-semibold"
                        style={{ background: 'rgba(255,255,255,0.2)' }}
                    >
                        Position
                    </span>
                    <div className="font-semibold">{jd.title || 'Job Description'}</div>
                </div>
                <IconButton aria-label="close" onClick={onClose} sx={{ color: '#fff' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <div className="space-y-4">
                    <Section title="Overview">
                        <div className="space-y-2">
                            <InfoRow label="Company" value={jd.company_name} />
                            <InfoRow label="Department" value={jd.department_name} />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <InfoRow label="Experience Level" value={jd.experience_level} />
                                <InfoRow label="Employment Type" value={jd.employment_type} />
                                <InfoRow label="Work Type" value={jd.work_type} />
                                {jd.application_deadline && (
                                    <InfoRow label="Application Deadline" value={new Date(jd.application_deadline).toLocaleString()} />
                                )}
                            </div>
                        </div>
                    </Section>

                    <Section title="Job Description">
                        <div className="prose max-w-none text-gray-800">
                            {jd.description ? parse(jd.description) : 'No description available.'}
                        </div>
                    </Section>

                    {(jd.required_skills?.length || jd.nice_to_have_skills?.length) ? (
                        <Section title="Skills">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {jd.required_skills?.length > 0 && (
                                    <div>
                                        <div className="text-gray-900 font-medium mb-2">Required</div>
                                        <div className="flex flex-wrap gap-2">
                                            {jd.required_skills.map((s, idx) => (
                                                <span key={idx} className="px-2 py-1 rounded-md text-sm bg-blue-50 text-blue-700 border border-blue-200">
                                                    {s}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {jd.nice_to_have_skills?.length > 0 && (
                                    <div>
                                        <div className="text-gray-900 font-medium mb-2">Nice to Have</div>
                                        <div className="flex flex-wrap gap-2">
                                            {jd.nice_to_have_skills.map((s, idx) => (
                                                <span key={idx} className="px-2 py-1 rounded-md text-sm bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                    {s}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Section>
                    ) : null}

                    {(jd.salary_range_min || jd.salary_range_max) && (
                        <Section title="Compensation">
                            <InfoRow label="Salary Range" value={`${jd.salary_range_min || '-'} - ${jd.salary_range_max || '-'}`} />
                        </Section>
                    )}

                    {(jd.public_link_display || jd.public_link) && (
                        <Section title="Public Posting">
                            <InfoRow label="Public URL" value={
                                <a href={jd.public_link || jd.public_link_display} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                                    {jd.public_link || jd.public_link_display}
                                </a>
                            } />
                        </Section>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}


